import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { faSignOut, faUser } from '@fortawesome/pro-regular-svg-icons';
import { AppTopbarUser } from './app-topbar-user';
import { Icon } from '../ui/icon';
import { cn } from '../../utils';
import { logout } from '../../../auth/oauth';
import { getNewAdminUrl } from '../../utils/urls';

interface AppTopbarUserMenuProps {
  tenantId: string;
  className?: string;
}

/**
 * User menu dropdown component for the topbar.
 * Provides access to user profile and sign out functionality.
 * Uses Backbone navigation for internal routes.
 */
export function AppTopbarUserMenu({
  tenantId,
  className,
}: AppTopbarUserMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    right: number;
  } | null>(null);

  // Calculate menu position when opening
  useEffect(() => {
    if (isOpen && triggerRef.current) {
      const updatePosition = () => {
        if (triggerRef.current) {
          const rect = triggerRef.current.getBoundingClientRect();
          setMenuPosition({
            top: rect.bottom + 8, // 8px = mt-2 (margin-top)
            right: window.innerWidth - rect.right,
          });
        }
      };

      // Calculate position immediately
      updatePosition();

      // Update on scroll/resize
      window.addEventListener('scroll', updatePosition, true);
      window.addEventListener('resize', updatePosition);

      return () => {
        window.removeEventListener('scroll', updatePosition, true);
        window.removeEventListener('resize', updatePosition);
      };
    }
    setMenuPosition(null);
  }, [isOpen]);

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsOpen(false);
      setIsClosing(false);
    }, 150); // Match animation duration
  };

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        triggerRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !triggerRef.current.contains(event.target as Node)
      ) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isOpen, handleClose]);

  const handleToggle = () => {
    if (isOpen) {
      handleClose();
    } else {
      setIsOpen(true);
      setIsClosing(false);
    }
  };

  const handleSignOut = async () => {
    await logout();
    window.location.href = '/';
  };

  const menuContent =
    isOpen && menuPosition ? (
      <div
        ref={menuRef}
        className={`tw-fixed tw-w-56 tw-rounded-lg tw-bg-white tw-border tw-shadow-lg tw-py-2 ${
          isClosing ? 'user-menu-dropdown-exit' : 'user-menu-dropdown-enter'
        }`}
        style={{
          zIndex: 10050,
          borderColor: '#e5e7eb',
          top: `${menuPosition.top}px`,
          right: `${menuPosition.right}px`,
          position: 'fixed',
          overflow: 'visible',
          backgroundColor: '#ffffff',
        }}
      >
        {/* My Account header */}
        <div className="tw-px-3 tw-py-2 tw-text-xs tw-font-semibold tw-text-gray-500 tw-uppercase tw-tracking-wide">
          My Account
        </div>

        <div
          className="tw-border-t tw-my-1"
          style={{ borderTopColor: '#e5e7eb' }}
        />

        {/* Profile */}
        <button
          id="topbar-menu-item-profile"
          onClick={() => {
            handleClose();
            const legacyAdminUrl = getNewAdminUrl();
            window.location.href = `${legacyAdminUrl}/org/${tenantId}/settings/profile`;
          }}
          className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-gray-700 hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors"
          style={{ backgroundColor: '#ffffff', fontSize: '14px' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#ffffff';
          }}
        >
          <Icon icon={faUser} className="tw-h-4 tw-w-4" />
          Profile
        </button>

        <div
          className="tw-border-t tw-my-1"
          style={{ borderTopColor: '#e5e7eb' }}
        />

        {/* Log out */}
        <button
          id="topbar-menu-item-logout"
          onClick={handleSignOut}
          className="tw-w-full tw-flex tw-items-center tw-gap-2 tw-px-3 tw-py-2 tw-text-red-600 hover:tw-bg-gray-50 tw-cursor-pointer tw-transition-colors"
          style={{ backgroundColor: '#ffffff', fontSize: '14px' }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#f9fafb';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#ffffff';
          }}
        >
          <Icon icon={faSignOut} className="tw-h-4 tw-w-4" />
          Log out
        </button>
      </div>
    ) : null;

  return (
    <>
      <div className={cn('tw-relative', className)}>
        <button
          id="topbar-menu-trigger"
          ref={triggerRef}
          onClick={handleToggle}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          aria-label="Account"
          className={cn(
            'tw-flex tw-items-center tw-rounded-full tw-transition-colors'
          )}
          style={{
            backgroundColor:
              isOpen || isHovered ? 'rgb(243 244 246 / 1)' : 'transparent',
            padding: '2px',
          }}
        >
          <AppTopbarUser />
        </button>
      </div>
      {menuContent && ReactDOM.createPortal(menuContent, document.body)}
    </>
  );
}
